import React from 'react';
import { Avatar, Badge, BottomSheet, Button } from '@parsina/framework';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation, MIRROR_ICON } from '@parsina/framework/clinical/src/i18n/index';
import { fmtCoins } from '@parsina/framework/clinical/src/constants/creditPricing';
import { creditColor } from '@parsina/framework/clinical/src/constants/credit';
import { ChevronLeft, MessagesSquare, UserRound, CreditCard, Globe } from 'lucide-react';
import { useReceptionStrings } from '../../lib/receptionStrings';
import { useMyPageStrings } from '../../screens/MyPage/myPageStrings';

// ─────────────────────────────────────────────────────────────────────────────
// «بیشتر» — what the phone's bottom bar folds away, the clinic panel's pattern
// (clinic-app `MobileMoreSheet`): who am I (name + پارسه balance) → where else
// can I go (پیام‌ها with its unread count, پروفایل, اعتبار).
//
// The bar keeps the doctor's working day — پذیرش · سوابق · حسابداری — and
// everything visited less often lives here, one tap further away.
//
// A row CLOSES the sheet and navigates only after the sheet has given its
// history entry back: the kit sheet owns an entry for the platform back
// gesture and releases it with `history.back()` on close. Navigating first
// would let that deferred back pop the DESTINATION — every row would close the
// menu and leave the doctor where they were (the bug the clinic app shipped).
// ─────────────────────────────────────────────────────────────────────────────

const MobileMoreSheet = ({ open, onClose, onNavigate, doctor, balance, unread }) => {
  const { t, formatNumber } = useTranslation();
  const rs = useReceptionStrings();
  const mp = useMyPageStrings();

  const go = (key) => {
    let done = false;
    const leave = () => {
      if (done) return;
      done = true;
      window.removeEventListener('popstate', leave);
      onNavigate(key);
    };
    window.addEventListener('popstate', leave);
    onClose();
    // No entry to give back (history integration unavailable) → no popstate.
    setTimeout(leave, 400);
  };

  const rows = [
    { key: 'messages', label: t('nav.items.messages'), Icon: MessagesSquare, badge: unread || undefined },
    { key: 'my-page',  label: mp('nav.items.myPage'),  Icon: Globe },
    { key: 'profile',  label: t('nav.items.profile'),  Icon: UserRound },
    { key: 'credits',  label: t('nav.items.credits'),  Icon: CreditCard },
  ];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={rs('nav.mobile.moreTitle')}
      closeLabel={t('common.actions.close')}
      data-testid="mobile-more-sheet"
    >
      <div style={{ display: 'grid', gap: '1rem', paddingBottom: '0.5rem' }}>

        {/* ── Who am I ─────────────────────────────────────────────── */}
        <section aria-label={rs('nav.mobile.account')} style={{ display: 'grid', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Avatar name={doctor?.name || '…'} size={44} src={doctor?.avatar || undefined} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div dir="auto" style={{ fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doctor?.name || '…'}
              </div>
              {doctor?.specialty && (
                <div dir="auto" style={{ fontSize: '0.75rem', color: C.textSecondary }}>{doctor.specialty}</div>
              )}
            </div>
          </div>

          {/* The balance only when it could be read — a 0 nobody can act on
              is worse than no line. */}
          {balance != null && (
            <Button
              variant="ghost"
              data-testid="mobile-more-credit"
              className="h-auto min-h-11 w-full justify-between rounded-xl px-3 py-2 font-normal"
              style={{ background: soft(creditColor(balance), 0.1) }}
              onClick={() => go('credits')}
            >
              <span style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{rs('nav.mobile.balance')}</span>
              <span style={{ fontSize: '0.9375rem', fontWeight: 800, color: creditColor(balance) }}>
                {fmtCoins(balance, formatNumber)} <span style={{ fontSize: '0.75rem', fontWeight: 500, color: C.textSecondary }}>{t('common.units.coin')}</span>
              </span>
            </Button>
          )}
        </section>

        {/* ── Where else ───────────────────────────────────────────── */}
        <section aria-label={rs('nav.mobile.sections')} style={{ display: 'grid', gap: '0.125rem' }}>
          <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted, padding: '0 0.25rem' }}>
            {rs('nav.mobile.sections')}
          </div>
          {rows.map(({ key, label, Icon, badge }) => (
            <Button
              key={key}
              variant="ghost"
              data-testid={`mobile-more-nav-${key}`}
              className="h-auto min-h-11 w-full justify-start gap-3 rounded-xl px-1.5 py-2 font-normal"
              onClick={() => go(key)}
            >
              <Icon size={18} aria-hidden style={{ color: C.textSecondary, flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: 'start', fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary }}>{label}</span>
              {badge ? <Badge variant="count">{badge}</Badge> : null}
              <ChevronLeft size={16} aria-hidden style={{ color: C.textMuted, flexShrink: 0, ...MIRROR_ICON }} />
            </Button>
          ))}
        </section>
      </div>
    </BottomSheet>
  );
};

export default MobileMoreSheet;
